import { inject, Injectable, signal } from "@angular/core";
import { DateTime } from "luxon";
import { CookieService } from "ngx-cookie-service";
import {
  INGXLoggerConfig,
  INGXLoggerMetadata,
  NgxLoggerLevel,
} from "ngx-logger";
import { lastValueFrom } from "rxjs";
import { VERSION } from "../../../environments/version";
import { LogService } from "../services/log.service";

// const DB_KEY_DATE = "log_flush_date";
// const AUTO_INTERVAL = 300000;
const FLUSH_THRESHOLD: number = 30; // logs count before flush
const FLUSH_NOLOG_DELAY: number = 300; // flush last logs after 5 mins if log length < threshold and no new log

@Injectable({
  providedIn: "root",
})
export class LogStore {
  private logService: LogService = inject(LogService);
  private cookieService: CookieService = inject(CookieService);

  private flushTimer: number = null;

  private readonly logs = signal<string[]>([]);
  private readonly flushing = signal<boolean>(false);

  public constructor() {
    window.onbeforeunload = () => {
      // try to flush logs before close tab or browser (if logged)
      this.flush();
    };
  }

  public onLog(logObject: INGXLoggerMetadata, config: INGXLoggerConfig): void {
    if (logObject.level < NgxLoggerLevel.INFO) return;

    // prepare log message
    const logData = [];
    logData.push(`${logObject.timestamp}`); // timestamp
    logData.push(`-- ${NgxLoggerLevel[logObject.level]} -- `); // log level
    if (this._getAdminOnBehalf()) {
      logData.push(`(ON-BEHALF=${this._getAdminOnBehalf()}) `); // admin
    }
    logData.push(
      `${logObject.message} ${
        logObject.additional ? logObject.additional.join(" ") : ""
      }`
    ); // message
    const newLog = logData.join("");

    // add log to store
    this.logs.set([...this.logs(), newLog]);

    // send logs if necessary
    if (this.logs().length > FLUSH_THRESHOLD) {
      // flush N logs to server
      this.flush();
    } else {
      // flush last logs after N seconds if not new log
      this.flushTimer = setTimeout(() => {
        this.flush();
      }, FLUSH_NOLOG_DELAY * 1000);
    }
  }

  public async flush() {
    if (this.flushing()) return;

    if (this.flushTimer) clearTimeout(this.flushTimer);

    if (this.logs().length === 0) return;

    this.flushing.set(true);

    // remove logs from store
    const logs = [...this.logs()];
    this.logs.set([]);

    // send logs
    const filename = "my-" + this._getUniqueId();
    const content = this._prepareFileContent(logs);
    try {
      await lastValueFrom(this.logService.sendLogs(filename, content));
    } catch (err) {
      console.error("flush logs Failed", err);
    }
    this.flushing.set(false);
  }

  // ----- PRIVATE METHODS

  private _prepareFileContent(logs: string[]): string {
    let contents = [...this._getFileHeaders(), ...logs];

    return contents.join("\n");
  }

  private _getAdminOnBehalf(): string {
    return this.cookieService.get("admin_on_behalf");
  }

  private _getRefreshToken(): string {
    return localStorage.getItem("refresh_token");
  }

  private _getUniqueId(): number {
    let hashCode = function (s: string) {
      var h = 0,
        l = s.length,
        i = 0;
      if (l > 0) while (i < l) h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
      return h;
    };
    let uniqueSource = this._getRefreshToken() || this._generateUUID();

    return Math.abs(hashCode(uniqueSource));
  }

  private _generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        let r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  private _getFileHeaders(): string[] {
    return [
      `user agent : ${window.navigator.userAgent}`,
      `resolution : ${window.innerWidth}x${window.innerHeight}`,
      `version : ${VERSION.version} - ${VERSION.hash}`,
      `date upload : ${DateTime.local().toISO()}`,
    ];
  }
}
